import React, { FunctionComponent, useState } from 'react'
import { useSelector } from 'react-redux'
import { DataGridFiltersWrapper, FieldsWrapper, FiltersSlideDown, ToolbarToggleWrapper } from './filtersStyles'
import { TextInput } from 'view/components/inputs'
import {
  getTextFlagButtonState,
  TextButton,
  TextButtonType,
  TextFlagButtonsContainer,
  ToolbarTextFlagButton
} from 'view/components/buttons'
import { DealSubscriptionIdentifier, EntityTypeEnum, FilterOperation, SubscriptionFilter, ValidationIssue } from 'api'
import { useChangedSubscriptionFilters } from 'view/shared/entity/hooks/useChangedSubscriptionFilters'
import { authorisedUserSelector } from 'selectors/connectionSelectors'
import { areOnlyDefaultSubscriptionFiltersActive } from 'model'
import { Locale, Translation } from 'locale'
import { SubscriptionDateRangeComponent, SubscriptionDates } from './SubscriptionFilterComponents/SubscriptionDateRange'
import { SubscriptionDetail } from 'store/selection/selectionTypes'
import {
  currentAndActiveSubscriptionFiltersAreEqual,
  getDateRangeSubscriptionFilters,
  getDateRangeSubscriptionFilterValue,
  getSubscriptionFilterValue,
  getUpdatedSubscriptionFilters
} from './utils'
import { isNotEmpty } from 'utils/predicate'
import { isEmpty } from 'lodash'

interface DealSubscriptionFiltersProps {
  subscriptionDetail: SubscriptionDetail | undefined
  dealSubscriptionIdentifier: DealSubscriptionIdentifier
}

const dealsSubscriptionFiltersFieldNames = ['tradedAt', 'startDate', 'tradeId', 'axeId']

export const DealSubscriptionFiltersComponent: FunctionComponent<DealSubscriptionFiltersProps> = ({
  dealSubscriptionIdentifier,
  subscriptionDetail
}) => {
  const authorisedUser = useSelector(authorisedUserSelector)
  const activeSubscriptionFilters = subscriptionDetail?.filters || []
  const activeDealSubscriptionFilters = activeSubscriptionFilters.filter(activeSubscriptionFilter =>
    dealsSubscriptionFiltersFieldNames.includes(activeSubscriptionFilter.fieldName)
  )

  const onChangeSubscription = useChangedSubscriptionFilters(EntityTypeEnum.DEAL, dealSubscriptionIdentifier, true)

  const isTodayFilterActive = areOnlyDefaultSubscriptionFiltersActive(dealSubscriptionIdentifier, activeSubscriptionFilters, authorisedUser)

  const [subscriptionFilters, setSubscriptionFilters] = useState<SubscriptionFilter[]>(activeDealSubscriptionFilters)
  const [validationIssues, setValidationIssues] = useState<ValidationIssue[]>([])

  const searchButtonDisabled =
    currentAndActiveSubscriptionFiltersAreEqual(subscriptionFilters, activeDealSubscriptionFilters) ||
    isNotEmpty(validationIssues) ||
    isEmpty(subscriptionFilters)

  const handleSubscribeToDefaults = () => {
    onChangeSubscription(null)
  }

  const handleUnsubscribe = () => {
    onChangeSubscription([])
  }

  const handleSubscribeHistorical = () => {
    onChangeSubscription(subscriptionFilters)
  }

  const handleUpdateFilterValue = (fieldName: string, filterOperation: FilterOperation) => (value: string | null) => {
    const updatedSubscriptionFilters = getUpdatedSubscriptionFilters(subscriptionFilters, fieldName, value, filterOperation)
    setSubscriptionFilters(updatedSubscriptionFilters)
  }

  const handleDateRangeUpdate = (fieldName: string, isDateAndTimeFilter = false) => (
    subscriptionDates: SubscriptionDates,
    validationIssues: ValidationIssue[]
  ) => {
    setValidationIssues(validationIssues)
    setSubscriptionFilters(getDateRangeSubscriptionFilters(subscriptionDates, fieldName, subscriptionFilters, isDateAndTimeFilter))
  }

  return (
    <DataGridFiltersWrapper>
      <ToolbarToggleWrapper>
        <div>{!isTodayFilterActive && Locale.translate(Translation.SC_FILTER_SEARCH_BY)}</div>
        <TextFlagButtonsContainer>
          <ToolbarTextFlagButton
            dataTestId={'DealSubscriptionFilters-ToolbarTextFlagButton-ButtonToday'}
            state={getTextFlagButtonState(isTodayFilterActive)}
            onClick={handleSubscribeToDefaults}>
            {Locale.translate(Translation.TC_FILTER_OPEN)}
          </ToolbarTextFlagButton>
          <ToolbarTextFlagButton
            dataTestId={'DealSubscriptionFilters-ToolbarTextFlagButton-SearchButton'}
            state={getTextFlagButtonState(!isTodayFilterActive)}
            onClick={handleUnsubscribe}>
            {Locale.translate(Translation.TC_FILTER_SEARCH)}
          </ToolbarTextFlagButton>
        </TextFlagButtonsContainer>
      </ToolbarToggleWrapper>
      <FiltersSlideDown closed={isTodayFilterActive}>
        <div>
          <FieldsWrapper>
            <SubscriptionDateRangeComponent
              label={Translation.TC_FILTER_TRADE_DATE}
              onUpdate={handleDateRangeUpdate('tradedAt', true)}
              initialSubscriptionDates={getDateRangeSubscriptionFilterValue(subscriptionFilters, 'tradedAt')}
            />
          </FieldsWrapper>
          <FieldsWrapper>
            <SubscriptionDateRangeComponent
              label={Translation.TC_START_DATE}
              onUpdate={handleDateRangeUpdate('startDate')}
              initialSubscriptionDates={getDateRangeSubscriptionFilterValue(subscriptionFilters, 'startDate')}
            />
          </FieldsWrapper>
          <FieldsWrapper>
            <label>{Locale.translate(Translation.SC_FILTER_TRADE_ID_EQUALS)}</label>
            <TextInput
              dataTestId={'DealSubscriptionFilters-TextInput-TradeIdEquals'}
              value={getSubscriptionFilterValue(subscriptionFilters, 'tradeId')}
              onUpdate={handleUpdateFilterValue('tradeId', FilterOperation.CONTAINS)}
              autoCapitalise={true}
            />
            <label>{Locale.translate(Translation.SC_FILTER_AXE_ID_EQUALS)}</label>
            <TextInput
              dataTestId={'DealSubscriptionFilters-TextInput-AxeIdEquals'}
              value={getSubscriptionFilterValue(subscriptionFilters, 'axeId')}
              onUpdate={handleUpdateFilterValue('axeId', FilterOperation.CONTAINS)}
              autoCapitalise={true}
            />
          </FieldsWrapper>
        </div>
        <TextButton
          dataTestId={'DealSubscriptionFilters-TextInput-ButtonSearch'}
          type={TextButtonType.Primary}
          disabled={searchButtonDisabled}
          onClick={handleSubscribeHistorical}>
          {Locale.translate(Translation.TC_BUTTON_SEARCH)}
        </TextButton>
      </FiltersSlideDown>
    </DataGridFiltersWrapper>
  )
}
